import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Phone, ArrowUpRight } from 'lucide-react';
import { POSTS, COUPONS, SITE } from '../../data/site';
import { toast } from 'sonner';
import { Reveal } from '../common/Reveal';

export const ScheduleCta = () => (
  <section className="py-24 bg-background" data-testid="schedule-cta-section">
    <div className="max-w-[1320px] mx-auto px-4 lg:px-8">
      <Reveal>
        <div className="relative overflow-hidden rounded-3xl bg-dark text-center px-6 py-16 md:py-20">
          <div className="absolute -top-28 right-0 w-[420px] h-[420px] rounded-full opacity-25 blur-3xl pointer-events-none" style={{ background: 'var(--gradient-fire)' }} />
          <div className="absolute -bottom-32 -left-16 w-[380px] h-[380px] rounded-full opacity-15 blur-3xl pointer-events-none" style={{ background: 'var(--gradient-fire)' }} />
          <div className="relative max-w-2xl mx-auto">
            <span className="eyebrow justify-center !text-[hsl(var(--primary-glow))]">Your dream roof is just a call away!</span>
            <h2 className="section-title mt-4 text-dark-foreground" data-testid="schedule-cta-title">Schedule A Free Estimate</h2>
            <p className="mt-5 text-lg text-dark-foreground/75">
              Choose <strong className="text-dark-foreground">Phoenix Roofing & Chimney</strong> for reliable, affordable, and expert roofing services in New Jersey. Don’t wait — <strong className="text-dark-foreground">save up to 40% now</strong>!
            </p>
            <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
              <Link to="/free-estimate" className="btn-cta" data-testid="schedule-cta-btn">
                Request a Free Estimate <ArrowRight className="w-4 h-4" />
              </Link>
              <a href={SITE.phoneHref} className="btn-ghost-light">
                <Phone className="w-4 h-4" /> {SITE.phone}
              </a>
            </div>
            <p className="mt-5 font-heading font-semibold text-sm text-dark-foreground/70">Free Inspections – Same Day Service</p>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

export const RecentPosts = () => (
  <section className="py-24 bg-secondary" data-testid="recent-posts-section">
    <div className="max-w-[1320px] mx-auto px-4 lg:px-8">
      <Reveal className="flex flex-wrap items-end justify-between gap-5">
        <div>
          <span className="eyebrow">From the Blog</span>
          <h2 className="section-title mt-4" data-testid="recent-posts-title">Recent Posts</h2>
        </div>
        <Link to="/blog" className="inline-flex items-center gap-1.5 font-heading font-bold text-primary hover:underline underline-offset-4">
          View all posts <ArrowUpRight className="w-4 h-4" />
        </Link>
      </Reveal>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
        {POSTS.map((p, i) => (
          <Reveal key={p.slug} delay={(i % 3) * 0.07}>
            <article className="h-full rounded-2xl bg-card border border-border overflow-hidden flex flex-col group card-lift" data-testid={`post-card-${p.slug}`}>
              <Link to={`/${p.slug}`} className="overflow-hidden">
                <img src={p.image} alt={p.title} className="w-full h-[200px] object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
              </Link>
              <div className="p-6 flex flex-col flex-1">
                <span className="chip w-fit">{p.category}</span>
                <h3 className="font-heading font-bold text-lg mt-4 leading-snug">
                  <Link to={`/${p.slug}`} className="hover:text-primary transition-colors">{p.title}</Link>
                </h3>
                <Link to={`/${p.slug}`} className="mt-auto pt-4 inline-flex items-center gap-1.5 font-heading font-bold text-sm text-primary">
                  Read article <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
                </Link>
              </div>
            </article>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const Coupons = () => {
  const claim = (c) => {
    const claimed = JSON.parse(localStorage.getItem('claimedCoupons') || '[]');
    localStorage.setItem('claimedCoupons', JSON.stringify([...claimed, { ...c, at: Date.now() }]));
    toast.success(`Coupon claimed: ${c.headline} ${c.bold}`, {
      description: `Mention this coupon when you call ${SITE.phone}.`,
    });
  };
  return (
    <section className="py-20 bg-background" data-testid="coupons-section">
      <div className="max-w-[1320px] mx-auto px-4 lg:px-8">
        <Reveal className="text-center max-w-xl mx-auto mb-12">
          <span className="eyebrow justify-center">Special Offers</span>
          <h2 className="section-title mt-4">Coupons & Savings</h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {COUPONS.map((c, i) => (
            <Reveal key={c.bold} delay={(i % 4) * 0.06}>
              <div className="coupon-card h-full rounded-2xl bg-card p-6 flex flex-col text-center" data-testid={`coupon-card-${i}`}>
                <div className="rounded-xl text-primary-foreground font-heading font-extrabold text-3xl py-4 tracking-tight" style={{ background: 'var(--gradient-fire)' }}>
                  {c.headline}
                </div>
                <p className="mt-5 text-muted-foreground text-[15px]">
                  <strong className="text-foreground">{c.bold}</strong> {c.text}
                </p>
                <button onClick={() => claim(c)} className="btn-cta-dark w-full mt-auto !py-3" data-testid={`claim-coupon-${i}`}>
                  Claim Coupon
                </button>
                <p className="text-muted-foreground text-xs mt-4">
                  Discount can not be combined. Only one (1) time offer.
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
